// src/features/admin/hooks/useAdminAuth.js
import { useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../../services/firebase';

// Strict role verification - only checks Firestore role field
const fetchAdminRole = async (user) => {
  if (!user) return false;

  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      // Document doesn't exist yet - don't sign out, just return false
      // This allows customer auth to create the document
      return false;
    }
    
    const userData = userDoc.data();
    // Strict check: role must be exactly 'admin'
    return userData?.role === 'admin';
  } catch (error) {
    // If there's a permission error, don't sign out - might be a customer
    // Only log the error, don't treat it as a reason to sign out
    if (error.code === 'permission-denied') {
      console.warn('Permission denied checking admin role - user might be a customer:', error);
      return false;
    }
    console.error('Error fetching admin role:', error);
    return false;
  }
};

export const useAdminAuth = () => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkRoute = () => {
      const currentPath = window.location.pathname;
      return currentPath.startsWith('/admin') && currentPath !== '/admin/login';
    };

    // Only set up listener if we're on an admin route
    // This prevents interfering with customer authentication
    const isAdminRoute = checkRoute();
    
    if (!isAdminRoute) {
      // Not on admin route - don't interfere with customer auth
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Double-check we're still on admin route
      if (!checkRoute()) {
        // User navigated away from admin route - don't interfere
        setLoading(false);
        return;
      }

      if (!user) {
        setUser(null);
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      // We're on an admin route - verify admin role
      try {
        const isAdminUser = await fetchAdminRole(user);
        if (!isAdminUser) {
          // User is on admin route but not an admin
          // Double-check we're still on admin route before signing out
          if (checkRoute()) {
            // Only sign out if we're actively on an admin route
            // This prevents signing out customers who briefly trigger the listener
            await signOut(auth);
            setUser(null);
            setIsAdmin(false);
            setError('Access denied. Your account does not have admin privileges.');
          } else {
            // User navigated away from admin route - don't sign out
            // Just don't set as admin
            setUser(null);
            setIsAdmin(false);
          }
        } else {
          // User is an admin - set them as authenticated admin
          setUser(user);
          setIsAdmin(true);
          setError(null);
        }
      } catch (err) {
        console.error('Failed to verify admin role:', err);
        // Don't sign out on error - might be a temporary issue
        // Just don't grant admin access
        setUser(null);
        setIsAdmin(false);
        // Only set error if we're still on admin route
        if (checkRoute()) {
          setError('Unable to verify admin access. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      // Use email/password authentication (NOT Google)
      const result = await signInWithEmailAndPassword(auth, email, password);

      // Immediately verify role in Firestore
      const hasAdminRole = await fetchAdminRole(result.user);
      if (!hasAdminRole) {
        // Strict enforcement: sign out immediately if role !== 'admin'
        await signOut(auth);
        throw new Error('Access denied. Your account does not have admin privileges. Role must be "admin" in Firestore.');
      }

      setUser(result.user);
      setIsAdmin(true);
      return result.user;
    } catch (error) {
      console.error('Sign in error:', error);
      
      // Handle specific Firebase Auth errors
      let errorMessage = error.message;
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No admin account found with this email address.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This admin account has been disabled.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection.';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email or password.';
          break;
        default:
          errorMessage = `Login failed: ${error.message}`;
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const signOutAdmin = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsAdmin(false);
      setError(null);
    } catch (error) {
      setError(error.message || 'Failed to sign out');
      throw error;
    }
  };

  const clearError = () => setError(null);

  return {
    user,
    isAdmin,
    loading,
    error,
    signIn,
    signOut: signOutAdmin,
    clearError
  };
};